import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { useContext, useState } from 'react';
import { AppContext } from 'context/app-context';
import { setModal } from 'store/features/modelSlice';
import { initModal } from 'helpers/utils';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import InputField from 'components/common/input-field';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import { createDeviceProvider, updateDeviceProvider } from 'store/features/vehicleConfigSlice';
import toast from 'react-hot-toast';
import { IDeviceProvider } from 'interfaces/vehicle-config-interfaces';
import { useTranslation } from 'react-i18next';

const AddEditDeviceProvider = () => {
  const dispatch = useAppDispatch();
  const {
    modal: {
      content: { title, formFor, callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { selectedDeviceProvider } = useAppSelector(state => state.vehicleConfig);
  const { setLoader } = useContext(AppContext);
  const [deviceProvider, setDeviceProvider] = useState<IDeviceProvider>(
    formFor === 'EDIT' && selectedDeviceProvider ? selectedDeviceProvider : { name: '' },
  );
  const [error, setError] = useState<string>('');
  const { t } = useTranslation();
  const handleAddDeviceProvider = async () => {
    try {
      setLoader(true);
      await dispatch(createDeviceProvider({ name: deviceProvider?.name }))
        .then(() => {
          callBackFun && callBackFun();
          toast.success(
            t('workshopAppointment.device_provider.successDeviceProviderNameAddedToastMessage'),
          );
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleEditDeviceProvider = async () => {
    try {
      setLoader(true);
      await dispatch(
        updateDeviceProvider({
          provider_id: deviceProvider._id || '',
          request: { name: deviceProvider?.name },
        }),
      )
        .then(() => {
          callBackFun && callBackFun();
          toast.success(
            t('workshopAppointment.device_provider.successDeviceProviderNameUpdatedToastMessage'),
          );
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSubmit = () => {
    try {
      if (!deviceProvider?.name?.trim()) {
        setError(t('workshopAppointment.device_provider.errorDeviceProviderNameToastMessage'));
        return;
      }
      formFor === 'EDIT' ? handleEditDeviceProvider() : handleAddDeviceProvider();
    } catch (error) {
      setLoader(false);
    }
  };
  return (
    <div className="flex flex-wrap max-w-[450px] w-[450px] gap-y-4 overflow-hidden">
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <label className="pl-2 text-xl font-semibold text-black2">{title}</label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => dispatch(setModal(initModal))}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full overflow-y-auto max-h-[calc(100vh- 200px)] gap-y-1">
        <div className="w-full">
          <label className="font-medium text-xs text-black2 w-full">
            {t('workshopAppointment.deviceProvider')}
          </label>
          <InputField
            onChange={data => setDeviceProvider({ ...deviceProvider, name: data })}
            error={error}
            value={deviceProvider.name}
            placeholder={t('workshopAppointment.device_provider.devicePlaceholder')}
          />
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-4">
        <PrimaryDarkButton
          label={
            formFor === 'EDIT'
              ? t('workshopAppointment.saveChanges')
              : t('workshopAppointment.addDeviceProvider')
          }
          onClick={() => handleSubmit()}
          width="w-1/2"
        />
        <DarkButton
          label={t('CANCEL')}
          width="w-1/2"
          onClick={() => dispatch(setModal(initModal))}
        />
      </div>
    </div>
  );
};

export default AddEditDeviceProvider;
